/**
 * Stem Estimate Display Helpers
 *
 * Shared helpers for rendering the "Est. Stems (Low/Medium/High)" rows that
 * appear in multiple places (hexagon accordion, hexagon summary preview,
 * per-detection breakdown). Centralizing the labels and null-handling here
 * avoids re-implementing the same three-row block with slightly different
 * (and easy to drift) logic in each component.
 *
 * @module stemEstimates
 */

import { formatNumber } from './helpers';
import { SQUARE_METERS_PER_HECTARE } from './measurementSystem';

/** Ordered low/medium/high levels with their display labels. */
export const STEM_ESTIMATE_LEVELS = [
	{ key: 'low', label: 'Est. Stems (Low)' },
	{ key: 'medium', label: 'Est. Stems (Medium)' },
	{ key: 'high', label: 'Est. Stems (High)' },
];

/**
 * Determines whether any stem estimate is present.
 *
 * Uses `!= null` rather than `> 0` so a genuine zero stem estimate
 * (eligible, but zero stems) is still considered "present" and rendered
 * as 0, instead of being treated the same as "no stem data available".
 *
 * @param {number|null|undefined} low
 * @param {number|null|undefined} medium
 * @param {number|null|undefined} high
 * @returns {boolean}
 */
export const hasStemEstimates = (low, medium, high) =>
	low != null || medium != null || high != null;

/**
 * Builds the low/medium/high rows ready for rendering, formatting each
 * value or falling back to '-' when that specific level has no data.
 *
 * When per-hectare density values are provided, the display format becomes:
 *   "42,344 / 7,561 pr ha"
 * This combined format shows foresters both the absolute count and the
 * density in a single row, which is the standard forestry reporting style.
 *
 * @param {number|null|undefined} low
 * @param {number|null|undefined} medium
 * @param {number|null|undefined} high
 * @param {Object} [options] - Optional configuration for density display.
 * @param {number|null|undefined} [options.perHaLow] - Per-hectare density for low estimate.
 * @param {number|null|undefined} [options.perHaMedium] - Per-hectare density for medium estimate.
 * @param {number|null|undefined} [options.perHaHigh] - Per-hectare density for high estimate.
 * @returns {Array<{ key: string, label: string, value: string }>}
 */
export const getStemEstimateRows = (low, medium, high, options = {}) => {
	const values = { low, medium, high };
	const perHaValues = {
		low: options.perHaLow,
		medium: options.perHaMedium,
		high: options.perHaHigh,
	};

	return STEM_ESTIMATE_LEVELS.map(({ key, label }) => ({
		key,
		label,
		value: formatStemValue(values[key], perHaValues[key]),
	}));
};

/**
 * Computes per-hectare density values for stem estimates given a raw area in m².
 *
 * Returns an options object compatible with `getStemEstimateRows`. When the area
 * is missing or invalid, returns an empty object so `getStemEstimateRows` gracefully
 * omits the density suffix.
 *
 * @param {{low?: number, medium?: number, high?: number}} stems - Stem estimate counts.
 * @param {number} area - Area in square meters (m²).
 * @returns {{perHaLow?: number, perHaMedium?: number, perHaHigh?: number}}
 */
export const computePerHaOptions = (stems, area) => {
	if (!stems || !area || area <= 0) return {};

	const hectares = area / SQUARE_METERS_PER_HECTARE;
	return {
		perHaLow: stems.low != null ? Math.round(stems.low / hectares) : undefined,
		perHaMedium: stems.medium != null ? Math.round(stems.medium / hectares) : undefined,
		perHaHigh: stems.high != null ? Math.round(stems.high / hectares) : undefined,
	};
};

/**
 * Formats a single stem estimate value, optionally appending the per-hectare density.
 *
 * @param {number|null|undefined} count - Absolute stem count.
 * @param {number|null|undefined} perHa - Per-hectare density (omitted if null/undefined).
 * @returns {string} Formatted display string, e.g. "42,344 / 7,561 pr ha" or "42,344" or "-".
 */
const formatStemValue = (count, perHa) => {
	if (count == null) return '-';

	const formattedCount = formatNumber(count, 0);

	if (perHa == null) return formattedCount;

	return `${formattedCount} / ${formatNumber(perHa, 0)} pr ha`;
};
